/**
 * Murmelie — Premium Interactions (Phase 2)
 * Subtle, restrained scroll-based animations
 * IntersectionObserver for fade-in reveals
 */
(function () {
  'use strict';

  /* Respect reduced motion */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  /* Skip in Shopify editor */
  if (window.Shopify && window.Shopify.designMode) return;

  /**
   * Fade-in on scroll using IntersectionObserver
   */
  function initFadeIn() {
    const elements = document.querySelectorAll('.murmelie-fade-in');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('murmelie-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFadeIn);
  } else {
    initFadeIn();
  }

  /**
   * Re-initialize when Shopify sections are loaded dynamically
   */
  document.addEventListener('shopify:section:load', function () {
    setTimeout(initFadeIn, 100);
  });
})();
/* =========================================================
   MURMELIE — PRODUCT MEDIA BY COLOR
   Global system for all products.
   ALT convention:
   Product Name - Color - 01
   Product Name - Color - 02
   Product Name - Color - 03
   ========================================================= */

(() => {
  const normalize = (text = '') =>
    text
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

  const getSelectedColor = () => {
    const selectors = document.querySelectorAll(
      '#MainContent variant-selects, #MainContent variant-radios'
    );

    for (const selector of selectors) {
      const fieldsets = selector.querySelectorAll('fieldset');

      for (const fieldset of fieldsets) {
        const legend = fieldset.querySelector('legend');
        if (!legend) continue;

        const label = normalize(legend.textContent);

        const isColor =
          label.includes('color') ||
          label.includes('colour') ||
          label.includes('couleur');

        if (!isColor) continue;

        const checked = fieldset.querySelector(
          'input[type="radio"]:checked'
        );

        if (checked) return checked.value.trim();
      }

      const selects = selector.querySelectorAll('select');

      for (const select of selects) {
        const wrapper = select.closest('.product-form__input');
        const label = normalize(
          wrapper?.querySelector('label, legend')?.textContent || ''
        );

        if (
          label.includes('color') ||
          label.includes('colour') ||
          label.includes('couleur')
        ) {
          return select.value.trim();
        }
      }
    }

    return null;
  };

  const belongsToColor = (item, color) => {
    const img = item.querySelector('img[alt]');
    if (!img) return false;

    const alt = normalize(img.getAttribute('alt'));
    const normalizedColor = normalize(color);

    return (
      alt.includes(` - ${normalizedColor} - `) ||
      alt.includes(` – ${normalizedColor} – `) ||
      alt.includes(` — ${normalizedColor} — `)
    );
  };

  const showItem = (item) => {
    item.hidden = false;
    item.style.removeProperty('display');
    item.removeAttribute('aria-hidden');
  };

  const hideItem = (item) => {
    item.hidden = true;
    item.style.setProperty('display', 'none', 'important');
    item.setAttribute('aria-hidden', 'true');
  };

  const refreshSlider = (component) => {
    if (!component) return;

    if (typeof component.resetPages === 'function') {
      component.resetPages();
    }
  };

  const filterMediaByColor = () => {
    const gallery = document.querySelector('#MainContent media-gallery');
    if (!gallery) return;

    const selectedColor = getSelectedColor();
    if (!selectedColor) return;

    const mediaItems = Array.from(
      gallery.querySelectorAll('.product__media-item')
    );

    const thumbnailItems = Array.from(
      gallery.querySelectorAll('.thumbnail-list__item')
    );

    const matchingMedia = mediaItems.filter((item) =>
      belongsToColor(item, selectedColor)
    );

    /*
     * FALLBACK:
     * Until a color has at least 2 tagged photos,
     * leave Shopify's normal gallery untouched.
     */
    if (matchingMedia.length < 2) {
      mediaItems.forEach(showItem);
      thumbnailItems.forEach(showItem);

      refreshSlider(gallery.elements?.viewer);
      refreshSlider(gallery.elements?.thumbnails);

      return;
    }

    mediaItems.forEach((item) => {
      belongsToColor(item, selectedColor)
        ? showItem(item)
        : hideItem(item);
    });

    thumbnailItems.forEach((item) => {
      belongsToColor(item, selectedColor)
        ? showItem(item)
        : hideItem(item);
    });

    /*
     * Important:
     * Dawn must recalculate its slider AFTER
     * non-matching images have been hidden.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewer = gallery.elements?.viewer;
        const thumbnails = gallery.elements?.thumbnails;

        refreshSlider(viewer);
        refreshSlider(thumbnails);

        const firstMedia = matchingMedia[0];

        if (
          firstMedia?.dataset.mediaId &&
          typeof gallery.setActiveMedia === 'function'
        ) {
          /*
           * false = do NOT reorder/prepend the DOM.
           * That was contributing to instability.
           */
          gallery.setActiveMedia(
            firstMedia.dataset.mediaId,
            false
          );
        }

        refreshSlider(viewer);
        refreshSlider(thumbnails);
      });
    });
  };

  const runFilter = () => {
    requestAnimationFrame(() => {
      filterMediaByColor();
    });
  };

  /* Initial page load */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runFilter);
  } else {
    runFilter();
  }

  /*
   * Dawn publishes variantChange AFTER it has finished
   * updating the variant media. This is the safest
   * moment to apply our filter.
   */
  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.variantChange
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      setTimeout(runFilter, 0);
    });
  } else {
    /*
     * Fallback only if Dawn's publish/subscribe
     * system is unavailable.
     */
    document.addEventListener('change', (event) => {
      if (
        event.target.closest(
          '#MainContent variant-selects, #MainContent variant-radios'
        )
      ) {
        setTimeout(runFilter, 600);
      }
    });
  }
})();